import { Banknote, CheckCircle2, CreditCard } from 'lucide-react';
import { money } from './invoice-discount-utils';
import { DateTimePicker } from '../../components/ui/DatePicker';

const inputClass =
  'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

// Must match the backend PaymentMethod enum (lowercase).
const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
];

interface InvoicePaymentCardProps {
  /** Raw text in the amount field. */
  amount: string;
  onAmountChange: (raw: string) => void;
  mode: string;
  onModeChange: (mode: string) => void;
  reference: string;
  onReferenceChange: (value: string) => void;
  /** The most that can still be collected (total on create, outstanding on edit). */
  maxAmount: number;
  /** Amount being collected now (already resolved/capped). */
  collecting: number;
  /** Remaining balance after this payment. */
  balanceAfter: number;
  /** Already-paid amount shown in the header (edit page). */
  paidSoFar?: number;
  /** When true, show the "fully paid" state instead of the inputs. */
  fullyPaid?: boolean;
  /** Disable the amount input (e.g. no services selected yet). */
  disabled?: boolean;
  /** Label for the "pay everything" shortcut. */
  fullLabel?: string;
  /**
   * Date & time the money was received, as a `datetime-local` value in the
   * hospital timezone. Only rendered when `onPaymentDateChange` is supplied —
   * i.e. the role holds `billing:invoice:backdate` and an amount is entered.
   */
  paymentDate?: string;
  onPaymentDateChange?: (value: string) => void;
  /** Hospital timezone (Settings → General) used by the date picker. */
  timeZone?: string;
}

export function InvoicePaymentCard({
  amount,
  onAmountChange,
  mode,
  onModeChange,
  reference,
  onReferenceChange,
  maxAmount,
  collecting,
  balanceAfter,
  paidSoFar,
  fullyPaid = false,
  disabled = false,
  fullLabel = 'Full amount',
  paymentDate,
  onPaymentDateChange,
  timeZone,
}: InvoicePaymentCardProps) {
  return (
    <section className="bg-surface border border-border rounded-2xl shadow-sm">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border flex-wrap">
        <div className="flex items-center gap-2">
          <Banknote size={17} className="text-primary" />
          <h2 className="text-sm font-bold text-text uppercase tracking-wide">Payment</h2>
        </div>
        <span className="text-xs text-text-muted">
          {typeof paidSoFar === 'number'
            ? `Already paid ${money(paidSoFar)}`
            : 'Optional — collect now'}
        </span>
      </div>
      <div className="p-5 space-y-4">
        {fullyPaid ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/20 text-sm text-success">
            <CheckCircle2 size={16} />
            This invoice is fully paid.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text mb-1">Amount received</label>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={amount}
                  onChange={(e) => onAmountChange(e.target.value)}
                  disabled={disabled}
                  className={`${inputClass} disabled:opacity-50`}
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => onAmountChange(String(maxAmount))}
                    disabled={maxAmount <= 0}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    {fullLabel}
                  </button>
                  <span className="text-text-muted text-xs">·</span>
                  <button
                    type="button"
                    onClick={() => onAmountChange('')}
                    disabled={!amount}
                    className="text-xs font-medium text-text-muted hover:text-text disabled:opacity-50"
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-1">Payment mode</label>
                <select
                  value={mode}
                  onChange={(e) => onModeChange(e.target.value)}
                  className={inputClass}
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {mode !== 'cash' && (
              <div>
                <label className="block text-sm font-medium text-text mb-1">Reference number</label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => onReferenceChange(e.target.value)}
                  placeholder="Txn / cheque reference"
                  className={inputClass}
                />
              </div>
            )}
            {/* Payment date — only for roles that may backdate, and only once an
                amount is entered. The collected amount is posted to the ledger
                and cashbook on this date. */}
            {onPaymentDateChange && collecting > 0 && (
              <div>
                <label className="block text-sm font-medium text-text mb-1">Payment date &amp; time</label>
                <DateTimePicker
                  value={paymentDate || ''}
                  onChange={onPaymentDateChange}
                  timeZone={timeZone}
                />
                <p className="mt-1 text-[0.6875rem] leading-4 text-text-muted">
                  The collected amount is posted on this date.
                </p>
              </div>
            )}
            <div className="flex items-center justify-between text-sm rounded-lg bg-background/60 border border-border px-3 py-2">
              <span className="flex items-center gap-1.5 text-text-muted">
                <CreditCard size={14} />
                Collecting now
              </span>
              <span className="font-medium text-text">{money(collecting)}</span>
            </div>
            <p className="text-xs text-text-muted">
              {typeof paidSoFar === 'number' ? 'Outstanding now: ' : 'Balance after payment: '}
              <span className="font-semibold text-text">
                {money(typeof paidSoFar === 'number' ? maxAmount : balanceAfter)}
              </span>
              {typeof paidSoFar === 'number' && (
                <>
                  {' · balance after payment: '}
                  <span className="font-semibold text-text">{money(balanceAfter)}</span>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
